import type { Recipe } from '../types';

// Freezer buttermilk waffles: a doubled, crisp-for-the-toaster version of the
// classic separated-egg buttermilk waffle. Cornstarch swapped in for some of
// the flour keeps them crisp after freezing. One quart of buttermilk, used up.

export const buttermilkWaffles: Recipe = {
  slug: 'buttermilk-waffles',
  title: 'Buttermilk Waffles',
  meal: 'breakfast',
  category: 'baked',
  types: ['breakfast', 'waffle'],
  cuisines: ['american'],
  traits: ['freezer-friendly', 'make-ahead', 'uses-up-leftovers'],
  yield: 'about 24 four-inch waffles — 12 servings',
  activeTime: '45 min',
  totalTime: '50 min',
  freezerLife: '3 months',
  difficulty: 2,

  summary:
    "Light, fluffy inside and crisp outside — then frozen flat so anyone can drop two in the toaster on a school morning. Whipped egg whites make them airy; a little cornstarch keeps them crisp after the freezer.",

  flavorNotes:
    "Tangy from the buttermilk, buttery, only lightly sweet (syrup does the sweetening), with a hint of vanilla. The outside should be golden brown and crunchy when you tap it; the inside soft and airy like a good pancake. Pale and floppy means the iron was not hot enough or they came out too soon. Dense and heavy means the egg whites got stirred flat — fold, don't stir.",

  ingredients: [
    {
      id: 'ap-flour',
      name: 'All-purpose flour',
      total: '3 cups',
      uses: [{ step: 2, amount: '3 cups' }],
    },
    {
      id: 'cornstarch',
      name: 'Cornstarch',
      total: '½ cup',
      uses: [{ step: 2, amount: '½ cup', note: 'the crispness trick' }],
    },
    {
      id: 'baking-powder',
      name: 'Baking powder',
      total: '1 tbsp',
      uses: [{ step: 2, amount: '1 tbsp' }],
    },
    {
      id: 'baking-soda',
      name: 'Baking soda',
      total: '2 tsp',
      uses: [{ step: 2, amount: '2 tsp' }],
    },
    {
      id: 'salt',
      name: 'Salt',
      total: '1 tsp',
      uses: [{ step: 2, amount: '1 tsp' }],
    },
    {
      id: 'eggs',
      name: 'Large eggs',
      total: '4',
      prep: 'separated — yolks in one bowl, whites in a clean, dry bowl',
      uses: [
        { step: 3, amount: '4 yolks' },
        { step: 5, amount: '4 whites' },
      ],
    },
    {
      id: 'butter',
      name: 'Unsalted butter',
      total: '1 cup (2 sticks)',
      prep: 'melted and cooled a few minutes',
      shop: 'Unsalted butter, 1 lb (4 sticks)',
      uses: [{ step: 3, amount: '1 cup' }],
    },
    {
      id: 'buttermilk',
      name: 'Whole buttermilk',
      total: '3½ cups',
      prep: 'full-fat cultured',
      shop: 'Whole cultured buttermilk, 1 quart',
      uses: [{ step: 3, amount: '3½ cups' }],
    },
    {
      id: 'vanilla',
      name: 'Pure vanilla extract',
      total: '2 tsp',
      uses: [{ step: 3, amount: '2 tsp' }],
    },
    {
      id: 'sugar',
      name: 'Sugar',
      total: '¼ cup',
      uses: [{ step: 5, amount: '¼ cup', note: 'whipped into the whites' }],
    },
    {
      id: 'avocado-oil',
      name: 'Avocado oil',
      total: '1 tbsp',
      prep: 'for brushing the waffle iron',
      uses: [{ step: 6, amount: '1 tbsp' }],
    },
    {
      id: 'maple-syrup',
      name: 'Pure maple syrup',
      total: 'for serving',
      optional: true,
      optionalKind: 'garnish',
      optionalWhy: 'Real maple syrup is one ingredient. Pancake syrup is corn syrup with caramel color.',
      shop: '100% pure maple syrup, 12.5 oz bottle',
      uses: [{ step: 6, amount: 'to taste', note: 'at the table' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Heat the iron and set up a cooling rack',
      time: '5 min',
      ingredientIds: [],
      body:
        "Turn the waffle iron on to medium-high and let it heat all the way up (most have a ready light). Set a wire cooling rack inside a sheet pan. Finished waffles go on the rack in one layer, never stacked — stacking traps steam and turns them soft.",
    },
    {
      n: 2,
      title: 'Whisk the dry',
      time: '2 min',
      ingredientIds: ['ap-flour', 'cornstarch', 'baking-powder', 'baking-soda', 'salt'],
      body:
        "In a big bowl whisk {3 cups|ap-flour} of flour, {½ cup|cornstarch} of cornstarch, {1 tbsp|baking-powder} of baking powder, {2 tsp|baking-soda} of baking soda and {1 tsp|salt} of salt.",
    },
    {
      n: 3,
      title: 'Separate the eggs and mix the wet',
      time: '5 min',
      critical: true,
      ingredientIds: ['eggs', 'butter', 'buttermilk', 'vanilla'],
      body:
        "Separate {4|eggs} eggs: yolks into a medium bowl, whites into a clean, dry bowl (the mixer bowl if you have one). Whisk into the yolks {1 cup|butter} of melted butter, {3½ cups|buttermilk} of buttermilk and {2 tsp|vanilla} of vanilla.",
      tip:
        "Even a drop of yolk in the whites and they will not whip. Crack each egg over a small cup first, then tip the white into the big bowl — if one breaks, only that one is lost. Cold eggs separate more easily.",
    },
    {
      n: 4,
      title: 'Wet into dry — barely mix',
      time: '1 min',
      ingredientIds: [],
      body:
        "Pour the wet into the dry and stir with a spatula just until no dry flour is showing. Lumpy batter is correct. Stop there.",
    },
    {
      n: 5,
      title: 'Whip the whites and fold them in',
      time: '6 min',
      critical: true,
      ingredientIds: ['eggs', 'sugar'],
      body:
        "Beat the egg whites with a mixer on medium until foamy, then turn it to high and slowly add {¼ cup|sugar} of sugar. Keep beating until you get stiff peaks, glossy and thick, about 3 to 5 minutes. Scoop one third of the whites into the batter and stir to loosen it. Then fold in the rest gently: cut down through the middle with the spatula, scoop under and turn it over, rotating the bowl. A few white streaks left is fine.",
      tip:
        "This is what makes them light. Stirring in a circle pops the air bubbles and you get dense waffles. No mixer? A whisk works with patience — about 8 to 10 minutes of hard whisking.",
    },
    {
      n: 6,
      title: 'Cook until the steam stops',
      time: '25 min',
      ingredientIds: ['avocado-oil', 'maple-syrup'],
      body:
        "Brush the iron lightly with avocado oil. For a standard square iron use about ⅓ cup of batter per 4-inch square; for a round Belgian iron, about 1 heaping cup. Close it and cook 4 to 5 minutes, until the steam coming out the sides nearly stops and they are deep golden. Move each one to the rack. Brush the iron again every couple of batches. Serve the first ones hot with butter and maple syrup.",
      tip:
        "Don't open the iron early — the waffle tears in half. When steam stops, the water is cooked out and the outside is crisp.",
    },
    {
      n: 7,
      title: 'Cool and freeze flat',
      time: '1 hr',
      ingredientIds: [],
      body:
        "Let the rest cool completely on the rack, about 20 minutes. Lay them on a sheet pan in a single layer and freeze 1 hour until hard, then move them into gallon freezer bags. That is a flash freeze — they won't stick together, so you can pull out just two.",
    },
  ],

  freezing:
    "Cool completely on a rack, flash freeze on a sheet pan in one layer for 1 hour, then into gallon freezer bags with the air pressed out, a piece of parchment between layers if you like. Up to 3 months.",

  reheat: [
    {
      method: 'Toaster, from frozen',
      best: true,
      detail:
        "Straight from the freezer into the toaster on a medium-dark setting, one or two cycles, until hot and crisp. Thick Belgian waffles may need a second round.",
    },
    {
      method: 'Oven or air fryer',
      detail:
        "From frozen, in one layer: oven at 375°F for 8 to 10 minutes, or air fryer at 350°F for 4 to 5 minutes. Best for a whole family at once.",
    },
    {
      method: 'Microwave',
      detail:
        "Works in a pinch, 30 to 45 seconds, but they come out soft. Toast them after if you can.",
    },
  ],

  glossary: [
    {
      term: 'separate',
      plain:
        "Split each egg into its yolk (the yellow) and its white. Pass the yolk back and forth between the shell halves over a bowl, letting the white fall in.",
    },
    {
      term: 'stiff peaks',
      plain:
        "Egg whites beaten until thick and shiny. Lift the beaters out and the point that forms stands straight up instead of flopping over. That air is what makes the waffles light.",
    },
    {
      term: 'fold',
      plain:
        "A gentle mixing motion — down through the middle, scoop under, turn it over — that blends without knocking out the air. The opposite of stirring.",
    },
    {
      term: 'flash freeze',
      plain:
        "Freezing pieces spread out on a pan before bagging them, so they freeze separately instead of in one solid block.",
    },
  ],

  inspiration: [
    { label: "Sally's Baking Addiction — My favorite buttermilk waffles", url: 'https://sallysbakingaddiction.com/my-favorite-buttermilk-waffles' },
    { label: 'Nerds with Knives — Light and crispy buttermilk waffles', url: 'https://nerdswithknives.com/the-best-light-and-crispy-buttermilk-waffles/' },
    { label: 'Grandbaby Cakes — Buttermilk waffles', url: 'https://grandbaby-cakes.com/buttermilk-waffles' },
  ],

  whereWeVeered:
    "Built on Sally's separated-egg buttermilk waffle, doubled so one batch fills the freezer and uses a whole quart of buttermilk. From Nerds with Knives we took two tricks: swapping some flour for cornstarch, which keeps the outside crisp after freezing and toasting, and whipping the sugar into the egg whites instead of the batter so they hold more air. Sugar is cut from Sally's ⅔ cup (doubled) to ¼ cup, because syrup does the sweetening. No protein powder — it makes waffles dry and rubbery; the protein bites cover that job.",
};
